<template>
  <div id="app">
    <div class="status-card">
      <div class="status-logo">
        <svg width="100%" height="68" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 89.858 100">
        <defs>
          <linearGradient id="sampleGradient">
            <stop :offset="fullyOperational ? '20%' : nonOperational ? '20%' : '20%'" :stop-color="fullyOperational ? gradients.green.start : nonOperational ? gradients.red.start : gradients.yellow.stop" />
            <stop offset="100%" :stop-color="fullyOperational ? gradients.green.stop : nonOperational ? gradients.red.stop : gradients.yellow.start" />
          </linearGradient>
        </defs>
        <path :class="[fullyOperational ? 'green' : nonOperational ? 'red' : 'yellow']" d="M51.284,83.261l8.749-5.8a19.169,19.169,0,0,1-8.406-2.486L33.039,64.973a19.2,19.2,0,0,1-6.692-5.63l-.019,10.484c-.015,7.518,2.2,13.092,6.25,15.869L34.6,86.781C39.145,88.636,45.015,87.414,51.284,83.261Z" style="fill: #3bca51;" />
        <path :class="[fullyOperational ? 'green' : nonOperational ? 'red' : 'yellow']" d="M26.373,46.047a20.132,20.132,0,0,1,5.562-5.278L52.87,26.9a19.477,19.477,0,0,1,7.536-3.179l-7.343-3.952c-7.024-3.782-13.607-4.3-18.588-1.494l-1.569,1.039c-4.195,3.223-6.5,8.951-6.513,16.351Z" style="fill: #3bca51;" />
        <path d="M78.349,33.379l-7.361-3.963a19.476,19.476,0,0,1,1.5,8.041l-.048,25.116a20.089,20.089,0,0,1-1.352,7.572l8.681-5.751c6.093-4.036,9.595-9.039,10.047-14.247l0-2.237C89.351,42.269,85.306,37.124,78.349,33.379Z" fill="url(#sampleGradient)" />
        <path :class="[fullyOperational ? 'green' : nonOperational ? 'red' : 'yellow']" d="M22.328,69.819l.019-10.484.026-13.3.02-10.384a30.91,30.91,0,0,1,1.732-10.526l-9.5,6.3C4.81,37.925-.375,46.3.021,55s6.324,16.57,16.687,22.149l7.617,4.1A30.108,30.108,0,0,1,22.328,69.819Z" style="fill: #3bca51;" />
        <path d="M54.958,16.244,62.3,20.2l10.582,5.7,7.361,3.963a29.647,29.647,0,0,1,9.591,7.833l.022-11.4C89.881,14.5,85.982,5.9,78.881,2.079S62.457-.262,52.626,6.25l-9.508,6.3A29.46,29.46,0,0,1,54.958,16.244Z" fill="url(#sampleGradient)" />
        <path d="M81.974,67.729,73.293,73.48,62.242,80.8l-8.75,5.8a29.871,29.871,0,0,1-10.628,4.637l7.586,4.084c5.836,3.141,11.5,4.709,16.675,4.683a21.415,21.415,0,0,0,11-2.95c7.485-4.461,11.619-13.4,11.641-25.17L89.791,60.5A30.877,30.877,0,0,1,81.974,67.729Z" fill="url(#sampleGradient)" />
        </svg>
      </div>
      <div class="status-overall">
        <div class="status-overall-status">
          <div class="status-overall-status-text" :class="[fullyOperational ? 'green' : nonOperational ? 'red' : 'yellow']">
            <h1 v-if="fullyOperational">All systems operational</h1>
            <h1 v-if="nonOperational">Something wrong</h1>
            <h1 v-if="!fullyOperational && !nonOperational">Systems operational,</h1>
            <h1 v-if="!fullyOperational && !nonOperational">with {{ limitedOperationalAmount }} {{ limitedOperationalAmount === 'one' ? 'system' : 'systems'}} having an incident</h1>
          </div>
        </div>
        <div class="status-overall-last-fire" :class="[fullyOperational ? 'shiftGreen' : nonOperational ? 'shiftRed' : 'shiftYellow']">
          <h2>Last incident {{ lastIncidentCounter }} {{ lastIncidentCounter === 1 ? 'day' : 'days' }} ago</h2>
        </div>
      </div>
      <div class="status-per-system">
        <div class="status-system" v-for="system in systems" :key="system.name">
          <span>{{ system.name }}</span>
          <div class="status-system-icon" :class="[system.operationalStatus === 1 ? 'green' : system.operationalStatus === 2 ? 'yellow' : 'red']">
            <svg width="100%" height="13.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 89.858 100">
              <path d="M51.284,83.261l8.749-5.8a19.169,19.169,0,0,1-8.406-2.486L33.039,64.973a19.2,19.2,0,0,1-6.692-5.63l-.019,10.484c-.015,7.518,2.2,13.092,6.25,15.869L34.6,86.781C39.145,88.636,45.015,87.414,51.284,83.261Z" style="fill: #ffffff;" />
              <path d="M26.373,46.047a20.132,20.132,0,0,1,5.562-5.278L52.87,26.9a19.477,19.477,0,0,1,7.536-3.179l-7.343-3.952c-7.024-3.782-13.607-4.3-18.588-1.494l-1.569,1.039c-4.195,3.223-6.5,8.951-6.513,16.351Z" style="fill: #ffffff;" />
              <path d="M78.349,33.379l-7.361-3.963a19.476,19.476,0,0,1,1.5,8.041l-.048,25.116a20.089,20.089,0,0,1-1.352,7.572l8.681-5.751c6.093-4.036,9.595-9.039,10.047-14.247l0-2.237C89.351,42.269,85.306,37.124,78.349,33.379Z" style="fill: #ffffff;"/>
              <path d="M22.328,69.819l.019-10.484.026-13.3.02-10.384a30.91,30.91,0,0,1,1.732-10.526l-9.5,6.3C4.81,37.925-.375,46.3.021,55s6.324,16.57,16.687,22.149l7.617,4.1A30.108,30.108,0,0,1,22.328,69.819Z" style="fill: #ffffff;" />
              <path d="M54.958,16.244,62.3,20.2l10.582,5.7,7.361,3.963a29.647,29.647,0,0,1,9.591,7.833l.022-11.4C89.881,14.5,85.982,5.9,78.881,2.079S62.457-.262,52.626,6.25l-9.508,6.3A29.46,29.46,0,0,1,54.958,16.244Z" style="fill: #ffffff;" />
              <path d="M81.974,67.729,73.293,73.48,62.242,80.8l-8.75,5.8a29.871,29.871,0,0,1-10.628,4.637l7.586,4.084c5.836,3.141,11.5,4.709,16.675,4.683a21.415,21.415,0,0,0,11-2.95c7.485-4.461,11.619-13.4,11.641-25.17L89.791,60.5A30.877,30.877,0,0,1,81.974,67.729Z" style="fill: #ffffff;"/>
            </svg>
            <!-- <span v-if="system.operationalStatus === 1" class="material-icons">done</span> -->
            <!-- <span v-else-if="system.operationalStatus === 2" class="material-icons">priority_high</span>
            <span v-else class="material-icons">close</span> -->
          </div>
        </div>
      </div>
      <!-- Current -->
      <h3 v-if="currentIncidents.length > 0" class="status-fire-heading">Current incidents</h3>
      <div v-if="currentIncidents.length > 0" class="status-fire-list">
        <div class="status-fire" v-for="(incident, index) in currentIncidents" :key="index">
          <div class="status-fire-date">
            <span class="status-fire-time">{{ moment(incident.date).format('dddd, MMMM Do YYYY, hh:mm A Z') }}</span>
            <vue-custom-tooltip :multiline="true" :label="generateFireDescription(incident.fire)"><div class="status-fire-icons-container">
              <span class="status-fire-icons" v-for="(fire, index) in incident.fire" :key="index">
                &#x1F525;
              </span>
            </div></vue-custom-tooltip>
          </div>
          <br />
          <div class="status-fire-description">
            {{ incident.description }}
          </div>
          <div class="status-fire-actions">
            <div class="status-fire-action" v-for="(action, index) in incident.actions" :key="index">
              <span class="status-fire-time">{{ moment(action.time).format('MMMM Do YYYY, hh:mm A Z') }}</span>
              <div class="status-fire-action-details">
                <span class="status-fire-action-status">{{ action.status }}</span> -
                <span class="status-fire-action-description">{{ action.description }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Past -->
      <h3 class="status-fire-heading">Incidents in the past 30 days</h3>
      <div class="status-fire-list">
        <div class="status-fire" v-for="(incident, incidentCounter) in fixedIncidents" :key="incidentCounter">
          <div class="status-fire-date">
            <span class="status-fire-time">{{ moment(incident.date).format('dddd, MMMM Do YYYY, hh:mm A Z') }}</span>
            <vue-custom-tooltip :multiline="true" :label="generateFireDescription(incident.fire)"><div class="status-fire-icons-container">
              <span class="status-fire-icons" v-for="(fire, index) in incident.fire" :key="index">
                &#x1F525;
              </span>
            </div></vue-custom-tooltip>
          </div>
          <br />
          <div class="status-fire-description">
            {{ incident.description }}
          </div>
          <div class="status-fire-show-hide-details">
            <span v-if="!expandContent.includes(incidentCounter)" @click="expandItem(incidentCounter)" class="material-icons">add</span>
            <span v-if="!expandContent.includes(incidentCounter)" @click="expandItem(incidentCounter)">Show details</span>
            <span v-if="expandContent.includes(incidentCounter)" @click="collapseItem(incidentCounter)" class="material-icons">remove</span>
            <span v-if="expandContent.includes(incidentCounter)" @click="collapseItem(incidentCounter)">Hide details</span>
          </div>
          <transition name="fade">
          <div class="status-fire-actions" v-show="expandContent.includes(incidentCounter)">
            <div class="status-fire-action" v-for="(action, index) in incident.actions" :key="index">
              <span class="status-fire-time">{{ moment(action.time).format('MMMM Do YYYY, hh:mm A Z') }}</span>
              <div class="status-fire-action-details">
                <span class="status-fire-action-status">{{ action.status }}</span> -
                <span class="status-fire-action-description">{{ action.description }}</span>
              </div>
            </div>
          </div>
          </transition>
        </div>
        <div class="status-fire" v-if="fixedIncidents.length === 0">
          No incidents
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { systems, incidents } from './data/status.js';

import moment from 'moment';

export default {
  name: 'App',
  data() {
    return {
      gradients: {
        green: {
          start: '#3bca51',
          stop: '#00ffff'
        },
        yellow: {
          start: '#fffe00',
          stop: '#ffa435',
        },
        red: {
          start: '#ff0020',
          stop: '#ff0ea7',
        }
      },
      systems,
      incidents,
      moment,
      expandContent: [],
    }
  },
  computed: {
    fullyOperational() {
      return this.systems.every(system => system.operationalStatus === 1);
    },
    limitedOperational() {
      return this.systems.some(system => system.operationalStatus === 2);
    },
    limitedOperationalAmount() {
      const systemsAmount = this.systems.filter(system => system.operationalStatus === 2).length;
      switch (systemsAmount) {
        case 1: return 'one';
        case 2: return 'two';
        case 3: return 'three';
        case 4: return 'four';
        case 5: return 'five';
        default: return '';
      }
    },
    nonOperational() {
      return this.systems.some(system => system.operationalStatus === 3);
    },
    fixedIncidents() {
      return this.incidents.filter(incident => incident.isFixed && moment().diff(incident.date, 'days') <= 30);
    },
    currentIncidents() {
      return this.incidents.filter(incident => !incident.isFixed);
    },
    lastIncidentCounter() {
      return this.currentIncidents.length > 0 ? 0 : incidents.length > 0 ? moment().diff(incidents[0].actions[incidents[0].actions.length - 1].time, 'days'): 0
    }
  },
  methods: {
    generateFireDescription(fire) {
      if (fire === 1) {
        return 'There is a low probability for adverse effects caused by this issue';
      } else if (fire === 2) {
        return 'There is a medium probability adverse effects caused by this issue';
      } else {
        return 'There is a high probability for adverse effects caused by this issue';
      }
    },
    expandItem(idx) {
      this.expandContent.push(idx);
    },
    collapseItem(idx) {
      this.expandContent = this.expandContent.filter(item => item !== idx);
    }
  }
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;700&display=swap');
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

body {
  background: #E5E5E5;

  font-family: 'Noto Sans', sans-serif;
  font-weight: 400;

  color: #515151;
}

h2 {
  font-weight: 500!important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

#app {

  svg {

    path.green {
      fill: #3bca51!important;
    }

    path.yellow {
      fill: #ffa435!important;
    }

    path.red {
      fill: #ff0020!important;
    }

  }

  .status-card {
    margin: 0 auto;

    margin-top: 50px;

    background: #FFFFFF;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, .25);

    width: 668px;

    .status-logo {
      padding-top: 64px;
      padding-bottom: 32px;
    }

    .status-overall {

      .status-overall-status {

        display: flex;
        align-items: center;
        justify-content: center;

        .status-overall-status-text {

          text-align: center;

          h1 {
            text-align: center;
            font-size: 22px;
          }

          &.green {
            h1 {
              color: #1ee4a8; // middle color
            }
          }

          &.yellow {
            h1 {
              color: #ffd11b; // middle color
            }

            h1:last-child {
              margin-top: -15px;
            }
          }

          &.red {
            h1 {
              color: #ff0764; // middle color
            }
          }
        }

      }

      .status-overall-last-fire {

        position: relative;
        top: -20px;

        margin-bottom: 40px;

        text-align: center;

        h2 {
          font-size: 14px;
          color: #333333;
        }
      }

    }

    .status-per-system {

      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      padding-bottom: 40px;

      color: #333333;

      .status-system {
        border: 1px solid #E5E5E5;

        width: 532px;

        display: flex;
        justify-content: space-between;
        align-items: center;

        padding-left: 16px;
        padding-top: 19px;
        padding-bottom: 24px;
        padding-right: 17px;

        .status-system-icon {
          display: flex;
          justify-content: space-between;
          align-items: center;

          margin-right: 18px;

          width: 26px;
          height: 26px;

          border-radius: 50%;

          color: white;

          span {
            line-height: 25px;
            font-size: 18px;
            font-weight: bold;
          }

          &.green {
            // background: #3bca51;
            background: linear-gradient(to right bottom, #3bca51 50%, #00ffff 100%);
          }

          &.yellow {
            // background: #ff6506;
            background: linear-gradient(to right bottom, #ffa435  50%, #fffe00 100%);
          }

          &.red {
            // background: #ff0629;
            background: linear-gradient(to right bottom, #ff0020 50%, #ff0ea7 100%);
          }
        }

      }

    }

    .status-fire-heading {
      margin-left: 54px;
      color: #333333;
      font-size: 18px;

      width: 567px;
      border-bottom: 1px solid #E5E5E5;
      padding-bottom: 25px;

      margin-bottom: 10px;
    }

    .status-fire-list {
      padding-top: 15px;
      padding-bottom: 15px;

      padding-right: 54px;
      text-align: justify;

      color: #333333;

      .status-fire {
        margin-left: 55px;
        margin-bottom: 30px;

        font-size: 14px;

        .status-fire-date {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .status-fire-description {
          margin-bottom: 10px;
        }

        .status-fire-show-hide-details {
          cursor: pointer;
          display: flex;
          align-items: center;
        }

        .status-fire-actions {
          margin-left: 10px;

          .status-fire-action {

            margin-top: 10px;

            .status-fire-action-details {
              padding-left: 15px;
              border-left: 3px solid #838383;

              margin-top: 10px;

              padding-top: 5px;
              padding-bottom: 5px;

              .status-fire-action-status {
                font-weight: 700;
              }
            }

          }

        }

      }
    }

  }

}
</style>
